// A row in a list of things to pick from: a carrier from the registry, or a
// contact we already hold.
//
// One shape for both, because they are the same gesture. The chosen row is
// tinted and shows a tick rather than a chevron, so what is selected reads
// without colour alone.

import { Check, ChevronRight } from 'lucide-react'

export interface ResultRowProps {
  name: string
  /** Facts under the name, each on its own line, in a fixed order. */
  details?: Array<string | null | undefined>
  selected?: boolean
  /** Draws the row as an action rather than a record — "Enter a different contact". */
  action?: boolean
  /** Marks the row as holding a value that cannot be used as it is. */
  invalid?: boolean
  /** The id of the message explaining why, read out with the row. */
  describedBy?: string
  onSelect: () => void
}

const join = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ')

export const ResultRow = ({
  name,
  details,
  selected = false,
  action = false,
  invalid = false,
  describedBy,
  onSelect,
}: ResultRowProps) => (
  <button
    type="button"
    onClick={onSelect}
    aria-pressed={selected}
    aria-describedby={describedBy}
    className={join(
      'flex w-full items-start justify-between gap-2 rounded-xl border px-3 py-2.5 text-left',
      invalid
        ? 'border-doubleNickel-error-500 bg-doubleNickel-error-25'
        : selected
        ? 'border-doubleNickel-brand-500 bg-doubleNickel-brand-25'
        : 'border-doubleNickel-gray-200 bg-white'
    )}
  >
    <div className="min-w-0 flex-1">
      <div
        className={join(
          'truncate text-[12.5px] font-semibold',
          action ? 'text-doubleNickel-brand-600' : 'text-doubleNickel-gray-900'
        )}
      >
        {name}
      </div>
      {details
        ?.filter((detail): detail is string => Boolean(detail && detail.trim()))
        .map((detail) => (
          <div
            key={detail}
            className="mt-px truncate text-[10.5px] text-doubleNickel-gray-500"
          >
            {detail}
          </div>
        ))}
    </div>
    {selected ? (
      <Check
        size={15}
        className="mt-0.5 flex-none text-doubleNickel-brand-600"
      />
    ) : (
      <ChevronRight
        size={15}
        className="mt-0.5 flex-none text-doubleNickel-gray-400"
      />
    )}
  </button>
)
